import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Typography,
    Box,
    LinearProgress,
} from "@mui/material";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function UpdateUserModalProfile({ show, handleClose, userId, token, fetchUsersData }) {
    const [formData, setFormData] = useState({
        fname: "",
        lname: "",
        address: "",
        weight: "",
        height: "",
        eyecolor: "",
        haircolor: "",
        children_ages: "",
        occupation: "",
        hobbies: "",
        personality: "",
        person_to_marry: "",
        yourself_in_future: "",
    });
    const [loading, setLoading] = useState(false);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [profilePhotoLoading, setProfilePhotoLoading] = useState(false);
    const [images, setImages] = useState([]);
    const [sliderGallery, setSliderGallery] = useState([]);
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [currentProfilePhoto, setCurrentProfilePhoto] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);
    const profilePhotoInputRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL;
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];

    // Fetch user data
    const fetchUserData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const user = response.data.data;
            setFormData({
                fname: user.fname || "",
                lname: user.lname || "",
                address: user.address || "",
                weight: user.weight || "",
                height: user.height || "",
                eyecolor: user.eyecolor || "",
                haircolor: user.haircolor || "",
                children_ages: user.children_ages || "",
                occupation: user.occupation || "",
                hobbies: user.hobbies || "",
                personality: user.personality || "",
                person_to_marry: user.person_to_marry || "",
                yourself_in_future: user.yourself_in_future || "",
            });
            setCurrentProfilePhoto(user.profile_pic ? `${API_URL}/${user.profile_pic}?t=${Date.now()}` : null);
            await getGalleryImages();
        } catch (error) {
            console.error("Fetch user error:", error);
            toast.error("Failed to load user data: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // Fetch gallery images
    const getGalleryImages = async () => {
        setGalleryLoading(true);
        try {
            const response = await axios.get(`${API_URL}/gallery/getbyid/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSliderGallery(response.data.data || []);
        } catch (error) {
            console.error("Fetch gallery error:", error);
            toast.error("Failed to fetch gallery images: " + (error.response?.data?.message || error.message));
        } finally {
            setGalleryLoading(false);
        }
    };

    useEffect(() => {
        if (userId && show) {
            fetchUserData();
        }
    }, [userId, show]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle profile form submission
    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.put(`${API_URL}/users/update/${userId}`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Profile details updated successfully!");
            fetchUsersData();
        } catch (error) {
            console.error("Profile update error:", error);
            toast.error("Failed to update profile: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload for gallery
    const handleFileUpload = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        const validFiles = selectedFiles.filter((file) => {
            if (!ALLOWED_FILE_TYPES.includes(file.type)) {
                toast.error(`Invalid file type for ${file.name}. Only JPEG, PNG, and GIF are allowed.`);
                return false;
            }
            if (file.size > MAX_FILE_SIZE) {
                toast.error(`File ${file.name} exceeds 25MB limit.`);
                return false;
            }
            return true;
        });

        if (validFiles.length > 0) {
            setImages(validFiles);
            toast.info(`${validFiles.length} image${validFiles.length > 1 ? "s" : ""} selected for upload`);
        } else {
            setImages([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    // Handle profile photo upload
    const handleProfilePhotoUpload = (event) => {
        const file = event.target.files[0];
        if (!file) {
            toast.error("Please select a profile photo.");
            return;
        }
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            toast.error(`Invalid file type for ${file.name}. Only JPEG, PNG, and GIF are allowed.`);
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            toast.error(`File ${file.name} exceeds 25MB limit.`);
            return;
        }
        setProfilePhoto(file);
        toast.info("Profile photo selected for upload");
    };

    // Handle gallery image submission
    const handleGallerySubmit = async (event) => {
        event.preventDefault();

        if (!images.length) {
            toast.error("Please select at least one valid image!");
            return;
        }

        const galleryFormData = new FormData();
        images.forEach((file) => {
            galleryFormData.append("images", file);
        });
        galleryFormData.append("user_id", userId);

        setGalleryLoading(true);
        try {
            const response = await axios.post(`${API_URL}/gallery/add/`, galleryFormData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                },
            });

            toast.success(`Successfully uploaded ${images.length} image${images.length > 1 ? "s" : ""}!`);
            setImages([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setUploadProgress(0);
            await getGalleryImages();
            fetchUsersData();
        } catch (error) {
            console.error("Gallery upload error:", error);
            toast.error("Failed to upload gallery images: " + (error.response?.data?.message || error.message));
        } finally {
            setGalleryLoading(false);
        }
    };

    // Handle profile photo submission
    const handleProfilePhotoSubmit = async (event) => {
        event.preventDefault();

        if (!profilePhoto) {
            toast.error("Please select a profile photo!");
            return;
        }

        const formData = new FormData();
        formData.append("profile_pic", profilePhoto);
        formData.append("user_id", userId);

        setProfilePhotoLoading(true);
        setUploadProgress(0);

        try {
            const response = await axios.post(`${API_URL}/users/profilepic/`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                },
            });
            toast.success("Profile photo uploaded successfully!");
            setProfilePhoto(null);
            if (profilePhotoInputRef.current) {
                profilePhotoInputRef.current.value = "";
            }
            setCurrentProfilePhoto(response.data.data?.profile_pic ? `${API_URL}/${response.data.data.profile_pic}?t=${Date.now()}` : null);
            setUploadProgress(0);
            fetchUsersData();
        } catch (error) {
            console.error("Profile photo upload error:", error);
            toast.error("Failed to upload profile photo: " + (error.response?.data?.message || error.message));
        } finally {
            setProfilePhotoLoading(false);
        }
    };

    // Handle gallery image deletion
    const handleRemoveGalleryImage = async (imageId) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;

        setGalleryLoading(true);
        try {
            await axios.delete(`${API_URL}/gallery/delete/${imageId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Image removed successfully!");
            await getGalleryImages();
            fetchUsersData();
        } catch (error) {
            console.error("Gallery delete error:", error);
            toast.error("Failed to delete image: " + (error.response?.data?.message || error.message));
        } finally {
            setGalleryLoading(false);
        }
    };

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Dialog open={show} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>Edit Profile</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box display="flex" justifyContent="center" padding="20px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Profile Form */}
                            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                Edit Profile Details
                            </Typography>
                            <form onSubmit={handleProfileSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            name="fname"
                                            value={formData.fname}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            name="lname"
                                            value={formData.lname}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Weight (kg)"
                                            name="weight"
                                            type="number"
                                            value={formData.weight}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Height (cm)"
                                            name="height"
                                            type="number"
                                            value={formData.height}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Eye Color"
                                            name="eyecolor"
                                            value={formData.eyecolor}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Hair Color"
                                            name="haircolor"
                                            value={formData.haircolor}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Children Ages"
                                            name="children_ages"
                                            value={formData.children_ages}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Occupation"
                                            name="occupation"
                                            value={formData.occupation}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Hobbies"
                                            name="hobbies"
                                            value={formData.hobbies}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Personality"
                                            name="personality"
                                            value={formData.personality}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Type of Man to Date/Marry"
                                            name="person_to_marry"
                                            value={formData.person_to_marry}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Future with the Right Man"
                                            name="yourself_in_future"
                                            value={formData.yourself_in_future}
                                            onChange={handleInputChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={loading || galleryLoading || profilePhotoLoading}
                                        >
                                            Save Profile
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>

                            {/* Gallery Section */}
                            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                Manage Gallery Images
                            </Typography>
                            {galleryLoading ? (
                                <Box display="flex" justifyContent="center" padding="20px">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box>
                                    <Grid container spacing={2}>
                                        {sliderGallery.length > 0 ? (
                                            sliderGallery.map((galleryData) => (
                                                <Grid item xs={6} sm={4} md={3} key={galleryData.id}>
                                                    <Box position="relative">
                                                        <img
                                                            src={`${API_URL}/${galleryData.image}?t=${Date.now()}`}
                                                            alt="Gallery"
                                                            style={{
                                                                width: "100%",
                                                                maxHeight: "150px",
                                                                objectFit: "cover",
                                                                borderRadius: "4px"
                                                            }}
                                                            onError={(e) => {
                                                                e.target.src = "/fallback-image.jpg";
                                                            }}
                                                        />
                                                        <Box
                                                            position="absolute"
                                                            top="5px"
                                                            right="5px"
                                                            bgcolor="red"
                                                            color="white"
                                                            borderRadius="50%"
                                                            width="24px"
                                                            height="24px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            sx={{ cursor: "pointer", "&:hover": { bgcolor: "darkred" } }}
                                                            onClick={() => handleRemoveGalleryImage(galleryData.id)}
                                                            aria-label="Delete image"
                                                        >
                                                            X
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            ))
                                        ) : (
                                            <Grid item xs={12}>
                                                <Typography textAlign="center" color="textSecondary">
                                                    No images in gallery
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                    <form onSubmit={handleGallerySubmit} style={{ marginTop: "20px" }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    type="file"
                                                    inputProps={{ multiple: true, accept: "image/jpeg,image/png,image/gif" }}
                                                    onChange={handleFileUpload}
                                                    inputRef={fileInputRef}
                                                    fullWidth
                                                    disabled={galleryLoading || loading || profilePhotoLoading}
                                                    helperText={images.length > 0 ? `${images.length} image${images.length > 1 ? "s" : ""} selected` : ""}
                                                />
                                            </Grid>
                                            {uploadProgress > 0 && galleryLoading && (
                                                <Grid item xs={12}>
                                                    <LinearProgress variant="determinate" value={uploadProgress} />
                                                    <Typography variant="caption">{uploadProgress}%</Typography>
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={!images.length || loading || galleryLoading || profilePhotoLoading}
                                                >
                                                    Upload Gallery Photos
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </Box>
                            )}

                            {/* Profile Photo Section */}
                            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                                Manage Profile Photo
                            </Typography>
                            <Box mb={3}>
                                {currentProfilePhoto ? (
                                    <Box mb={2}>
                                        <Typography variant="body1">Current Profile Photo:</Typography>
                                        <img
                                            src={`${currentProfilePhoto}`}
                                            alt="Current Profile"
                                            style={{
                                                width: "150px",
                                                height: "150px",
                                                objectFit: "cover",
                                                borderRadius: "8px",
                                                border: "1px solid #ddd"
                                            }}
                                            onError={(e) => {
                                                e.target.src = "/fallback-image.jpg";
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="body1" mb={2} color="textSecondary">
                                        No profile photo set
                                    </Typography>
                                )}
                                <form onSubmit={handleProfilePhotoSubmit}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                type="file"
                                                inputProps={{ accept: "image/jpeg,image/png,image/gif" }}
                                                onChange={handleProfilePhotoUpload}
                                                inputRef={profilePhotoInputRef}
                                                fullWidth
                                                disabled={profilePhotoLoading || loading || galleryLoading}
                                                helperText={profilePhoto ? profilePhoto.name : ""}
                                            />
                                        </Grid>
                                        {uploadProgress > 0 && profilePhotoLoading && (
                                            <Grid item xs={12}>
                                                <LinearProgress variant="determinate" value={uploadProgress} />
                                                <Typography variant="caption">{uploadProgress}%</Typography>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={!profilePhoto || profilePhotoLoading || loading || galleryLoading}
                                            >
                                                Upload Profile Photo
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        color="secondary"
                        disabled={loading || galleryLoading || profilePhotoLoading}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default UpdateUserModalProfile;